import { useCallback, useEffect, useRef } from 'react';
import { useLifecycles } from 'react-use';
import { AllCasesEventType, casesBoardNotifier, WsAllCasesEventMessageContent } from '@/ws/notifiers/cases-board';
import { caseQueryKeys, reconcileCasesByIds } from '@/queries/case';
import { queryClient } from '@/queries';

const BOARD_INVALIDATE_DEFAULT_INTERVAL_MS = 15_000;

export const useCasesBoardSubscription = (intervalMs = BOARD_INVALIDATE_DEFAULT_INTERVAL_MS) => {
  const idsRef = useRef<Set<string>>(new Set([]));

  const handler = useCallback(({ CaseId }: WsAllCasesEventMessageContent) => {
    idsRef.current.add(CaseId);
  }, []);

  useLifecycles(
    () => casesBoardNotifier.on(AllCasesEventType.CaseUpdated, handler),
    () => casesBoardNotifier.off(AllCasesEventType.CaseUpdated, handler),
  );

  const invalidateBoardCaches = useCallback(async () => {
    if (idsRef.current.size > 0) {
      const caseIds = Array.from(idsRef.current);
      idsRef.current.clear();

      await Promise.all([
        reconcileCasesByIds(caseIds),
        queryClient.invalidateQueries({ queryKey: caseQueryKeys.getCounts(), exact: false }),
      ]);
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(invalidateBoardCaches, intervalMs);

    return () => clearInterval(intervalId);
  }, [intervalMs, invalidateBoardCaches]);
};
